import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Smartphone, Share2, CheckCircle, ArrowRight, ExternalLink, Truck } from 'lucide-react';
import { toast } from 'sonner';

const SHARE_URL = 'https://justdeliver.ie/install';
const SHARE_TEXT =
  'Get the JustDeliver app — post a delivery, drivers bid, you choose. ';

/**
 * Public, no-auth install landing page designed to be shared on
 * WhatsApp / FB / Instagram / SMS / Gumtree / DoneDeal. Single shareable
 * link → one big install button. Platform-aware:
 *   - Android Chrome → triggers native PWA install prompt
 *   - iOS Safari    → "Share → Add to Home Screen" instructions
 *   - Desktop       → web-app link + QR-friendly URL
 */
export default function InstallPage() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [platform, setPlatform] = useState('other'); // 'ios' | 'android' | 'desktop' | 'other'

  useEffect(() => {
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (standalone) setInstalled(true);

    const ua = navigator.userAgent || '';
    const isIos = /iphone|ipad|ipod/i.test(ua) && !window.MSStream;
    const isAndroid = /android/i.test(ua);
    const isMobile = isIos || isAndroid || /mobile/i.test(ua);
    setPlatform(isIos ? 'ios' : isAndroid ? 'android' : isMobile ? 'other' : 'desktop');

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice.outcome === 'accepted') {
          toast.success('Installing JustDeliver…');
          setInstalled(true);
        }
        setDeferredPrompt(null);
      } catch {
        toast.error('Install failed — try opening this page in Chrome.');
      }
      return;
    }
    if (platform === 'ios') {
      // Scroll to instructions
      document.getElementById('ios-instructions')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    // Fallback: open the web app
    window.location.href = '/';
  };

  const handleShare = async () => {
    const data = { title: 'JustDeliver', text: SHARE_TEXT, url: SHARE_URL };
    if (navigator.share) {
      try { await navigator.share(data); } catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(`${SHARE_TEXT}${SHARE_URL}`);
      toast.success('Share link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-emerald-50/40 flex flex-col">
      {/* Top brand */}
      <header className="px-4 sm:px-6 py-5 flex items-center justify-between max-w-2xl mx-auto w-full">
        <Link to="/" className="flex items-center" data-testid="install-logo">
          <img src="/logo.png" alt="JustDeliver.ie" className="h-8 sm:h-10 w-auto object-contain" />
        </Link>
        <button
          onClick={handleShare}
          className="text-sm text-[#1B5E20] hover:text-[#2E7D32] font-medium inline-flex items-center gap-1.5"
          data-testid="install-share-btn"
        >
          <Share2 className="w-4 h-4" /> Share
        </button>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-5 sm:px-8 py-6 sm:py-10">
        {/* Hero */}
        <div className="text-center" data-testid="install-hero">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full bg-emerald-100 text-[#1B5E20] text-xs font-semibold">
            <Smartphone className="w-3.5 h-3.5" /> Mobile App · Free
          </div>
          <h1 className="text-3xl sm:text-5xl font-extrabold text-gray-900 leading-tight font-['Inter']">
            Install <span className="text-[#2E7D32]">JustDeliver</span>
          </h1>
          <p className="mt-3 text-base sm:text-lg text-gray-600 max-w-md mx-auto leading-snug">
            Post a delivery, drivers bid, you choose the best price. The JustDeliver app installs straight to your home screen — no app store needed.
          </p>
        </div>

        {/* Feature highlights */}
        <ul className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm" data-testid="install-features">
          {[
            'Live driver bids',
            'Push notifications',
            'Works offline',
          ].map((f) => (
            <li key={f} className="flex items-center gap-2 px-3 py-2 rounded-md bg-white border border-emerald-100">
              <CheckCircle className="w-4 h-4 text-[#2E7D32] flex-shrink-0" />
              <span className="text-gray-700 font-medium">{f}</span>
            </li>
          ))}
        </ul>

        {/* Big install CTA */}
        <div className="mt-8 bg-white border border-emerald-200 rounded-2xl shadow-sm p-5 sm:p-7 text-center" data-testid="install-cta-card">
          {installed ? (
            <>
              <CheckCircle className="w-12 h-12 text-[#2E7D32] mx-auto mb-3" />
              <h2 className="text-xl font-bold text-gray-900 mb-1">JustDeliver is installed!</h2>
              <p className="text-sm text-gray-600 mb-4">Launch it from your home screen, or keep using it here.</p>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-[#2E7D32] text-white font-semibold hover:bg-[#1B5E20] transition-colors"
                data-testid="install-open-app"
              >
                Open the app <ArrowRight className="w-4 h-4" />
              </Link>
            </>
          ) : (
            <>
              <button
                onClick={handleInstall}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-4 rounded-md bg-gradient-to-br from-[#2E7D32] to-[#1B5E20] text-white font-bold text-lg shadow-md hover:shadow-lg hover:from-[#1B5E20] hover:to-[#0e3e10] active:scale-[0.98] transition-all"
                data-testid="install-primary-btn"
              >
                <Download className="w-5 h-5" />
                {deferredPrompt ? 'Install JustDeliver App' : platform === 'ios' ? 'Add to Home Screen' : 'Install App'}
              </button>
              <p className="text-xs text-gray-500 mt-3">Free · No app store · Installs in seconds</p>
            </>
          )}
        </div>

        {/* iOS-specific instructions */}
        {platform === 'ios' && !installed && (
          <div id="ios-instructions" className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-5" data-testid="install-ios-instructions">
            <h3 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
              <Smartphone className="w-5 h-5" /> On iPhone / iPad
            </h3>
            <ol className="list-decimal list-inside space-y-1.5 text-sm text-amber-900/90">
              <li>Open this page in <strong>Safari</strong> (not Chrome).</li>
              <li>Tap the <strong>Share</strong> button (square + arrow).</li>
              <li>Scroll down and tap <strong>Add to Home Screen</strong>.</li>
              <li>Tap <strong>Add</strong> in the top right.</li>
            </ol>
          </div>
        )}

        {/* Android-specific instructions when the deferred prompt isn't available yet */}
        {platform === 'android' && !deferredPrompt && !installed && (
          <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-5" data-testid="install-android-tip">
            <h3 className="font-semibold text-emerald-900 mb-2 flex items-center gap-2">
              <Smartphone className="w-5 h-5" /> On Android
            </h3>
            <p className="text-sm text-emerald-900/90">
              In Chrome, tap the <strong>⋮ menu</strong> (top right) → <strong>Install app</strong> or <strong>Add to Home screen</strong>.
            </p>
          </div>
        )}

        {/* Desktop fallback */}
        {platform === 'desktop' && !installed && (
          <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-5 text-sm text-gray-700" data-testid="install-desktop-tip">
            <p className="mb-2 font-semibold text-gray-900">Visiting from desktop?</p>
            <p>JustDeliver is designed for mobile. Open <strong>https://justdeliver.ie/install</strong> on your phone, or use the web app below.</p>
            <Link to="/" className="mt-3 inline-flex items-center gap-1 text-[#1B5E20] hover:text-[#2E7D32] font-semibold">
              Use the web app <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}

        {/* Trust footer */}
        <div className="mt-10 text-center text-xs text-gray-400" data-testid="install-trust">
          <p>Already have an account? <Link to="/login" className="text-[#1B5E20] hover:underline font-semibold">Log in</Link></p>
          <p className="mt-1">
            <Link to="/terms" className="hover:underline">Terms</Link>
            <span className="mx-2">·</span>
            <Link to="/privacy" className="hover:underline">Privacy</Link>
          </p>
        </div>
      </main>
    </div>
  );
}
